/**
 * Emissions Reports Page
 *
 * Generate and manage emission reports.
 * Based on CarbonIQ Reports design pattern.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { ReportTemplateCards } from '../components/ReportTemplateCards';
import { RecentReportsList } from '../components/RecentReportsList';
import { CsvExportModal } from '../components/CsvExportModal';
import { SquareCardGrid, type SquareCardItem } from '../components/SquareCard';
import { EmissionsOnePagerModal } from '../features/one-pager/EmissionsOnePagerModal';
import { checkIsBottomOfInitiativeTree } from '@selectors/initiative';
import { useAppSelector } from '@reducers/index';
import {
  useGenerateEmissionsPPTXReportMutation,
  useGetEmissionsPPTXReportsQuery,
  useLazyGetEmissionsPPTXReportDownloadUrlQuery,
  useDeleteEmissionsPPTXReportMutation,
} from '../api/emissions';
import { type GeneratedReport, GeneratedReportStatus, ReportTemplateType } from '../types/emissions';

const ACTIVE_STATUSES = new Set(['pending', 'processing', 'waitForRun', 'paused']);

const buildCertificateItems = (onSelectOnePager: () => void): SquareCardItem[] => [
  {
    id: 'one-pager',
    title: 'Emissions One-Pager',
    description: 'Printable PDF and shareable image of your carbon footprint summary',
    icon: 'fa-file-image',
    iconBgColor: 'rgba(15, 118, 110, 0.1)',
    iconColor: '#0f766e',
    onClick: onSelectOnePager,
  },
  {
    id: 'ghg-certificate',
    title: 'GHG Certificate',
    description: 'Certificate of compliance with GHG Protocol reporting standards',
    icon: 'fa-certificate',
    iconBgColor: 'rgba(40, 167, 69, 0.1)',
    iconColor: '#28a745',
    comingSoon: true,
  },
  {
    id: 'csrd-certificate',
    title: 'CSRD Certificate',
    description: 'Certificate of compliance with EU Corporate Sustainability Reporting Directive',
    icon: 'fa-shield-check',
    iconBgColor: 'rgba(0, 123, 255, 0.1)',
    iconColor: '#007bff',
    comingSoon: true,
  },
];

export const EmissionsReportsPage = () => {
  const { initiativeId } = useParams<{
    initiativeId: string;
    reportType?: string;
  }>();

  const isLeafInitiative = useAppSelector(checkIsBottomOfInitiativeTree);
  const hasChildren = !isLeafInitiative;

  const currentYear = new Date().getFullYear();
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [showOnePagerModal, setShowOnePagerModal] = useState(false);

  const certificateItems = useMemo(
    () => buildCertificateItems(() => setShowOnePagerModal(true)),
    []
  );

  const dataExportItems: SquareCardItem[] = [
    {
      id: 'csv-export',
      title: 'Activity Data (CSV)',
      description: 'Export filtered activity records to CSV for use in spreadsheets or other tools',
      icon: 'fa-file-csv',
      iconBgColor: 'rgba(32, 201, 151, 0.1)',
      iconColor: '#20c997',
      onClick: () => setShowCsvModal(true),
    },
  ];

  const [periodStart, setPeriodStart] = useState(`${currentYear - 1}-01-01`);
  const [periodEnd, setPeriodEnd] = useState(`${currentYear - 1}-12-31`);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: pptxReports = [], refetch } = useGetEmissionsPPTXReportsQuery(
    { initiativeId: initiativeId ?? '' },
    { skip: !initiativeId }
  );

  const [generateReport, { isLoading: isGenerating }] = useGenerateEmissionsPPTXReportMutation();
  const [getDownloadUrl] = useLazyGetEmissionsPPTXReportDownloadUrlQuery();
  const [deleteReport] = useDeleteEmissionsPPTXReportMutation();

  const hasActiveReport = pptxReports.some((r) => ACTIVE_STATUSES.has(r.status));

  useEffect(() => {
    if (hasActiveReport) {
      pollingRef.current = setInterval(refetch, 10_000);
    } else if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [hasActiveReport, refetch]);

  const handleGenerate = async () => {
    if (!initiativeId) {
      return;
    }
    await generateReport({ initiativeId, periodStart, periodEnd });
    setShowGenerateModal(false);
  };

  const handleDeleteReport = async (reportId: string) => {
    if (!initiativeId) {
      return;
    }
    const report = pptxReports.find((r) => r._id === reportId);
    if (!report) {
      return;
    }
    await deleteReport({ initiativeId, jobId: report._id, taskId: report.taskId });
  };

  const handleDownloadReport = async (reportId: string) => {
    if (!initiativeId) {
      return;
    }
    const report = pptxReports.find((r) => r._id === reportId);
    if (!report) {
      return;
    }
    const result = await getDownloadUrl({ initiativeId, jobId: report._id, taskId: report.taskId });
    if (result.data?.downloadUrl) {
      window.open(result.data.downloadUrl, '_blank');
    }
  };

  // Map EmissionsPPTXReportItem → GeneratedReport shape for RecentReportsList
  const mappedReports: GeneratedReport[] = pptxReports.map((item) => {
    let status: GeneratedReportStatus;
    if (ACTIVE_STATUSES.has(item.status)) {
      status = GeneratedReportStatus.Processing;
    } else if (item.status === 'completed') {
      status = GeneratedReportStatus.Completed;
    } else {
      status = GeneratedReportStatus.Failed;
    }

    const fmt = (d?: string) =>
      d ? new Date(d).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '';
    const name =
      item.periodStart && item.periodEnd
        ? `Emissions Report ${fmt(item.periodStart)} – ${fmt(item.periodEnd)}`
        : 'Emissions Report';

    return {
      _id: item._id,
      initiativeId: item.initiativeId,
      name,
      templateType: ReportTemplateType.GHGProtocol,
      status,
      periodStart: item.periodStart ?? '',
      periodEnd: item.periodEnd ?? '',
      createdBy: item.user
        ? [item.user.firstName, item.user.surname].filter(Boolean).join(' ') || 'Unknown'
        : '',
      created: item.created,
      updated: item.created,
    };
  });

  return (
    <Dashboard>
      {/* Page Header */}
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <div>
          <h4 className='mb-1 fw-semibold'>Download Reports</h4>
          <p className='text-ThemeTextLight mb-0'>
            Generate and manage sustainability reports
          </p>
        </div>
      </div>

      {/* Report Templates */}
      <ReportTemplateCards
        enabledIds={['summary']}
        onSelectTemplate={() => setShowGenerateModal(true)}
      />

      <div className='tw:grid tw:grid-cols-5 tw:gap-4 mb-4'>
        {/* Data Exports — 2 columns */}
        <div className='tw:col-span-1'>
          <h5 className='text-uppercase text-ThemeTextLight mb-3'>Data Exports</h5>
          <SquareCardGrid items={dataExportItems} columns={1} />
        </div>
        {/* Certificates & Badges — 3 columns */}
        <div className='tw:col-span-3'>
          <h5 className='text-uppercase text-ThemeTextLight mb-3'>Certificates & Badges</h5>
          <SquareCardGrid items={certificateItems} columns={3} />
        </div>

      </div>

      {/* Recent Reports */}
      <RecentReportsList
        reports={mappedReports}
        isLoading={false}
        onDownload={handleDownloadReport}
        onDelete={handleDeleteReport}
      />

      {/* CSV Export Modal */}
      <CsvExportModal
        isOpen={showCsvModal}
        toggle={() => setShowCsvModal(false)}
        initiativeId={initiativeId ?? ''}
        hasChildren={hasChildren}
      />

      {/* Emissions One-Pager Modal */}
      <EmissionsOnePagerModal
        isOpen={showOnePagerModal}
        toggle={() => setShowOnePagerModal(false)}
        initiativeId={initiativeId ?? ''}
        hasChildren={hasChildren}
      />

      {/* Generate Modal */}
      <Modal isOpen={showGenerateModal} toggle={() => setShowGenerateModal(false)} centered>
        <ModalHeader toggle={() => setShowGenerateModal(false)}>
          Generate Emissions Report
        </ModalHeader>
        <ModalBody>
          <p className='text-ThemeTextLight mb-3'>
            A PPTX report will be generated in the background and ready to download when complete.
          </p>
          <div className='mb-3'>
            <label className='form-label fw-medium'>Period Start</label>
            <input
              type='date'
              className='form-control'
              value={periodStart}
              onChange={(e) => setPeriodStart(e.target.value)}
            />
          </div>
          <div className='mb-3'>
            <label className='form-label fw-medium'>Period End</label>
            <input
              type='date'
              className='form-control'
              value={periodEnd}
              onChange={(e) => setPeriodEnd(e.target.value)}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant='secondary' onClick={() => setShowGenerateModal(false)} disabled={isGenerating}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={isGenerating || !periodStart || !periodEnd}
          >
            {isGenerating
              ? <><span className='spinner-border spinner-border-sm me-1' />Starting…</>
              : 'Generate'
            }
          </Button>
        </ModalFooter>
      </Modal>
    </Dashboard>
  );
};
